import { useState, useEffect, useCallback } from "react";
import { Lock, Gem, Flame, ArrowRight, Loader2, ShieldAlert, TrendingUp, Crown, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";

// STAGE 7 · M3 Part 3 — Wealth pill rewards freeze + conversion preview.
// Outbound on-chain transfers are HARD-FROZEN (display only). Shows loyalty
// Credits + streak bars and the treasury-scaled Credits → AUD → MiLink preview.
function Bar({ pct, color }) {
  return (
    <div className="h-2.5 w-full rounded-full bg-black/10 overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, Math.min(100, pct))}%`, backgroundColor: color }} />
    </div>
  );
}

function fmtFiat(v) {
  const n = Number(v) || 0;
  if (n === 0) return "$0.00";
  if (n >= 0.01) return `$${n.toFixed(2)}`;
  // sub-cent: show enough precision to stay non-zero + visibly react to the slider
  return `$${n.toFixed(8).replace(/0+$/, "").replace(/\.$/, "")}`;
}

export default function WealthRewardsPanel({ onOpenBusinessPlan }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [price, setPrice] = useState(0.01); // hypothetical projected MLINK spot (AUD)
  const [xfer, setXfer] = useState("");
  const [moving, setMoving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/rewards/conversion")
      .then((r) => {
        setData(r.data);
        const spot = Number(r.data?.conversion?.spot_aud) || 0.01;
        setPrice(Math.min(1, Math.max(0.01, spot)));
      })
      .catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Part 1: intercept the transfer → open the permanent-lock confirmation modal.
  const requestTransfer = () => {
    const amt = Number(xfer);
    if (!amt || amt <= 0) { toast.error("Enter an amount to move"); return; }
    setConfirmOpen(true);
  };
  const confirmTransfer = async () => {
    const amt = Number(xfer);
    setMoving(true);
    try {
      const r = await api.post("/rewards/transfer-to-internal", { amount: amt });
      setData((d) => ({ ...d, state: r.data.state, conversion: r.data.conversion }));
      setXfer(""); setConfirmOpen(false);
      toast.success(`${r.data.moved} Credits locked to internal use`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
    finally { setMoving(false); }
  };

  if (err) return null;
  if (!data) {
    return <div className="grid place-items-center py-8" data-testid="wealth-rewards-loading"><Loader2 size={22} className="text-gold animate-spin" /></div>;
  }

  const st = data.state || {};
  const conv = data.conversion || {};
  const earned = st.earnedCredits ?? 0;
  const bought = st.boughtCredits ?? 0;
  const streak = st.loginStreak || 0;
  const nextMilestone = Math.max(100, (Math.floor(earned / 100) + 1) * 100);

  return (
    <div className="mb-4 space-y-3" data-testid="wealth-rewards-panel">
      {/* Freeze banner */}
      <div className="flex items-start gap-2 rounded-2xl border-2 border-amber-400 bg-amber-50 p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="wealth-freeze-banner">
        <Lock size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <p className="font-mono text-[10px] leading-relaxed text-amber-900">{data.notice}</p>
      </div>

      {/* Two-bucket loyalty ledger */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3.5 shadow-[3px_3px_0px_#1A1A1A] space-y-3" data-testid="wealth-loyalty">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border-2 border-emerald-500/50 bg-emerald-50 p-2.5" data-testid="wealth-earned-credits">
            <p className="font-mono text-[8px] uppercase tracking-widest text-emerald-700 flex items-center gap-1"><Gem size={10} /> Earned</p>
            <p className="font-sans font-black text-lg text-charcoal leading-none mt-1" data-testid="wealth-earned-value">{earned.toLocaleString()}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-emerald-700/70 mt-0.5">Ready to cash out</p>
          </div>
          <div className="rounded-xl border-2 border-charcoal/40 bg-neutral-100 p-2.5" data-testid="wealth-bought-credits">
            <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/60 flex items-center gap-1"><Lock size={10} /> Internal</p>
            <p className="font-sans font-black text-lg text-charcoal leading-none mt-1" data-testid="wealth-bought-value">{bought.toLocaleString()}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-charcoal/45 mt-0.5">Locked · internal only</p>
          </div>
        </div>
        <div className="space-y-1.5" data-testid="wealth-credits-bar">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-widest text-charcoal/60">Earned progress</span>
            <span className="font-mono text-[8px] text-charcoal/40">{Math.max(0, nextMilestone - earned).toLocaleString()} to {nextMilestone.toLocaleString()}</span>
          </div>
          <Bar pct={(earned / nextMilestone) * 100} color="#059669" />
        </div>
        <div className="space-y-1.5" data-testid="wealth-streak-bar">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-widest text-charcoal/60 flex items-center gap-1"><Flame size={11} className="text-rust" /> Login Streak</span>
            <span className="font-sans font-bold text-sm text-charcoal">{streak} day{streak === 1 ? "" : "s"}</span>
          </div>
          <Bar pct={(Math.min(streak, 7) / 7) * 100} color="#C1440E" />
        </div>
        {/* One-way earned → internal transfer (permanent lock) */}
        <div className="rounded-xl border-2 border-dashed border-charcoal/30 p-2.5 space-y-2" data-testid="wealth-transfer">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/55 flex items-center gap-1"><ArrowRightLeft size={10} /> Move Earned → Internal (permanent)</p>
            <button onClick={() => setXfer(String(earned))} data-testid="wealth-transfer-all" className="font-mono text-[8px] font-black uppercase tracking-widest text-emerald-700 underline decoration-emerald-500/60 underline-offset-2 active:opacity-70">Transfer All</button>
          </div>
          <div className="flex gap-1.5">
            <input value={xfer} onChange={(e) => setXfer(e.target.value)} inputMode="decimal" placeholder="Amount" data-testid="wealth-transfer-amount"
              className="flex-1 min-w-0 bg-white border-2 border-charcoal/40 rounded-lg px-2.5 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold" />
            <button onClick={requestTransfer} disabled={moving} data-testid="wealth-transfer-btn"
              className="shrink-0 flex items-center justify-center gap-1 bg-charcoal text-white font-mono text-[9px] font-black uppercase tracking-widest px-3 rounded-lg border-2 border-charcoal disabled:opacity-60">
              <Lock size={11} /> Lock In
            </button>
          </div>
          <p className="font-mono text-[7px] text-charcoal/40">Locked credits are internal-use only and leave the crypto cash-out pool permanently.</p>
        </div>
      </div>

      {/* Conversion preview */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3.5 shadow-[3px_3px_0px_#1A1A1A] space-y-3" data-testid="wealth-conversion-preview">
        <p className="font-mono text-[9px] uppercase tracking-widest text-charcoal/60">Conversion Preview · live-metric scaled</p>
        <div className="flex items-center gap-1.5">
          <div className="flex-1 text-center rounded-xl border-2 border-emerald-500/40 bg-emerald-50 py-2" data-testid="conv-credits">
            <p className="font-sans font-black text-base text-charcoal leading-none">{Number(conv.credits || 0).toLocaleString()}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-charcoal/50 mt-1">Credits</p>
          </div>
          <ArrowRight size={14} className="text-charcoal/40 shrink-0" />
          <div className="flex-1 text-center rounded-xl border-2 border-sky-500/40 bg-sky-50 py-2" data-testid="conv-aud">
            <p className="font-sans font-black text-base text-charcoal leading-none">${Number(conv.aud_value || 0).toFixed(2)}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-charcoal/50 mt-1">AUD</p>
          </div>
          <ArrowRight size={14} className="text-charcoal/40 shrink-0" />
          <div className="flex-1 text-center rounded-xl border-2 border-gold bg-yellow-50 py-2" data-testid="conv-token">
            <p className="font-sans font-black text-[13px] text-charcoal leading-none break-all">{Number(conv.token_fraction || 0).toFixed(6)}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-charcoal/50 mt-1">MiLink Cash</p>
          </div>
        </div>
        <p className="font-mono text-[8px] text-charcoal/40 text-center">spot ${Number(conv.spot_aud || 0).toFixed(4)} AUD · scaling tier {conv.tier} (÷{Number(conv.denominator || 0).toLocaleString()})</p>
        <button disabled data-testid="wealth-coming-soon-btn" className="w-full flex items-center justify-center gap-2 bg-charcoal/90 text-white/80 border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-3 rounded-full cursor-not-allowed opacity-90">
          <Lock size={13} /> Withdraw to Coin Wallet · Coming Soon
        </button>
        <p className="font-mono text-[8px] text-charcoal/40 text-center flex items-center justify-center gap-1"><ShieldAlert size={10} /> No live on-chain withdrawals are enabled.</p>
      </div>

      {/* Projection slider — retention mechanic */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3.5 shadow-[3px_3px_0px_#1A1A1A] space-y-3" data-testid="wealth-projection">
        <p className="font-mono text-[9px] uppercase tracking-widest text-charcoal/60 flex items-center gap-1"><TrendingUp size={11} className="text-emerald-600" /> Future Value Projection</p>
        <input
          type="range" min="0.01" max="1.00" step="0.01" value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          data-testid="wealth-projection-slider"
          className="w-full accent-gold h-2 cursor-pointer"
        />
        <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-wider text-charcoal/45">
          <span>Launch Floor ($0.01 AUD)</span>
          <span>Target Goal ($1.00 AUD)</span>
        </div>
        <div className="rounded-xl border-2 border-gold bg-yellow-50 p-3 text-center" data-testid="wealth-projection-output">
          <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/50">If MiLink Cash hits <span className="text-charcoal font-black" data-testid="wealth-projection-price">${price.toFixed(2)}</span> AUD</p>
          <p className="font-sans text-2xl font-black text-charcoal leading-none mt-1" data-testid="wealth-projection-value">{fmtFiat((Number(conv.token_fraction) || 0) * price)}</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/45 mt-1">projected payout · {(Number(conv.token_fraction) || 0).toFixed(6)} MiLink Cash</p>
        </div>
        <button
          type="button"
          onClick={onOpenBusinessPlan}
          data-testid="wealth-projection-tooltip"
          className="w-full text-left font-mono text-[8px] leading-relaxed text-charcoal/60 flex items-start gap-1.5 rounded-lg border border-transparent hover:border-gold hover:bg-yellow-50 active:bg-yellow-100 transition-colors p-1 -m-1 underline decoration-gold/60 underline-offset-2"
        >
          <Crown size={11} className="text-gold mt-0.5 shrink-0" />
          Unlock the Gold-Badge Business Plan to lock in premium multiplier rates.
        </button>
      </div>

      {/* Part 1: permanent lock-in confirmation modal */}
      {confirmOpen && (
        <div className="absolute inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" data-testid="transfer-confirm-modal">
          <div className="w-[328px] max-w-[calc(100vw-32px)] bg-cream border-4 border-charcoal rounded-2xl p-4 shadow-[5px_5px_0px_#1A1A1A] space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-rust shrink-0" />
              <h3 className="font-serif text-lg text-charcoal">Confirm — this can't be undone</h3>
            </div>
            <p className="font-mono text-[10px] leading-relaxed text-charcoal font-bold bg-red-50 border-2 border-red-300 rounded-lg p-2.5" data-testid="transfer-confirm-warning">
              ⚠️ CRITICAL WARNING: This action is 100% permanent. Moving these credits to Internal use locks them out of the MiLink crypto cash-out vault forever.
            </p>
            <p className="font-mono text-[9px] text-charcoal/60 text-center">Locking <span className="font-black text-charcoal">{Number(xfer) || 0}</span> Credits</p>
            <button onClick={confirmTransfer} disabled={moving} data-testid="transfer-confirm-yes"
              className="w-full flex items-center justify-center gap-2 bg-charcoal text-white font-mono text-[10px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
              {moving ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Yes, I understand
            </button>
            <button onClick={() => setConfirmOpen(false)} disabled={moving} data-testid="transfer-confirm-cancel"
              className="w-full flex items-center justify-center gap-2 bg-white text-charcoal border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-3 rounded-full disabled:opacity-60">
              Cancel / Keep as Crypto-Eligible
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
